import { showReportDialog } from '@sentry/browser'
import * as React from 'react'
import sentry from '../config/sentry'

const INITIAL_STATE = {
  componentStack: null,
  error: null,
  eventId: null,
}

export default class SentryErrorBoundary extends React.Component {
  state = INITIAL_STATE

  componentDidCatch(error, { componentStack }) {
    const { beforeCapture, onError, showDialog, dialogOptions } = this.props

    sentry.withScope((scope) => {
      const errorBoundaryError = new Error(error.message)
      errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`
      errorBoundaryError.stack = componentStack

      // Using the `LinkedErrors` integration to link the errors together.
      error.cause = errorBoundaryError

      if (beforeCapture) {
        beforeCapture(scope, error, componentStack)
      }
      const eventId = sentry.captureException(error, {
        contexts: { react: { componentStack } },
      })
      if (onError) {
        onError(error, componentStack, eventId)
      }
      if (showDialog) {
        showReportDialog({ ...dialogOptions, eventId })
      }

      // componentDidCatch is used over getDerivedStateFromError
      // so that componentStack is accessible through state.
      this.setState({ error, componentStack, eventId })
    })
  }

  componentDidMount() {
    const { onMount } = this.props
    if (onMount) {
      onMount()
    }
  }

  componentWillUnmount() {
    const { error, componentStack, eventId } = this.state
    const { onUnmount } = this.props
    if (onUnmount) {
      onUnmount(error, componentStack, eventId)
    }
  }

  resetErrorBoundary = () => {
    const { onReset } = this.props
    const { error, componentStack, eventId } = this.state
    if (onReset) {
      onReset(error, componentStack, eventId)
    }
    this.setState(INITIAL_STATE)
  }

  render() {
    const { fallback, children } = this.props
    const { error, componentStack, eventId } = this.state

    if (error) {
      let element = undefined
      if (typeof fallback === 'function') {
        element = fallback({
          error,
          componentStack,
          resetError: this.resetErrorBoundary,
          eventId,
        })
      } else {
        element = fallback
      }

      if (React.isValidElement(element)) {
        return element
      }

      // Fail gracefully if no fallback provided or is not valid
      return null
    }

    if (typeof children === 'function') {
      return children()
    }
    return children
  }
}
